import { FromToFilter, useFromToFilter, useRefreshCounter } from "./api.utils";
import {
  Alert,
  GetSCBOneConfigurationStateResponse,
  PagedResponse,
  Position,
  SCBOneDevice,
  SCBOneDevicesTableSorting,
  SCBOneOperationRun,
  DesiredSCBOneConfig,
  UpdateResult,
  ApiErrorResponse,
  SCBOneDeviceOrganization,
  ApiResponse,
} from "./models";
import {
  ApiCall,
  ApiState,
  PagedApiState,
  useApiState,
  useAuthorizedFetch,
  useGetAuthorized,
  usePagedApiState,
} from "./useApiState";

export function useSCBOneDevice(
  serial: string
): ApiState<SCBOneDevice | undefined> {
  return useApiState(
    useGetAuthorized<SCBOneDevice>(`scb-ones/${serial}`),
    undefined
  );
}

export function useSCBOneDevices(
  assignedToOrganization?: boolean,
  filterString?: string,
  sorting?: SCBOneDevicesTableSorting,
  pageSize?: number
): PagedApiState<SCBOneDevice> {
  const refreshCounter = useRefreshCounter();

  let alreadyHasQueryParameter = false;

  let query = "";

  filterString = filterString?.trim();

  if (filterString) {
    const prefix = alreadyHasQueryParameter ? "&" : "?";
    query += prefix + "filter_by=" + filterString;
    alreadyHasQueryParameter = true;
  }

  if (sorting?.sortColumnId !== undefined) {
    const prefix = alreadyHasQueryParameter ? "&" : "?";
    query +=
      prefix + "sort=" + sorting.sortColumnId + "&order=" + sorting.sortOrderId;
    alreadyHasQueryParameter = true;
  }

  if (assignedToOrganization !== undefined) {
    const prefix = alreadyHasQueryParameter ? "&" : "?";
    query += prefix + "assigned_to_organization=" + assignedToOrganization;
    alreadyHasQueryParameter = true;
  }

  const get = useGetAuthorized<PagedResponse<SCBOneDevice>>("scb-ones" + query);
  const devices = usePagedApiState(get, refreshCounter, pageSize);

  return devices;
}

export function useSCBOneOrganizationDevices(
  organizationId: string,
  sorting?: SCBOneDevicesTableSorting
): PagedApiState<SCBOneDeviceOrganization> {
  const refreshCounter = useRefreshCounter();
  let url = `organizations/${organizationId}/scb-ones`;

  // API only supports sort on serial column, no need to pass column to it
  if (sorting?.sortOrderId !== undefined) {
    url += "?order=" + sorting.sortOrderId;
  }

  const get = useGetAuthorized<PagedResponse<SCBOneDeviceOrganization>>(url);
  return usePagedApiState(get, refreshCounter);
}

export function useAssignSCBOneDeviceToOrganization(): ApiCall<
  { serials: string[]; organizationId: string },
  void
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (a: { serials: string[]; organizationId: string }) => {
    const result = await authorizedFetch(
      `organizations/${a.organizationId}/scb-ones`,
      { method: "put", body: JSON.stringify(a.serials) }
    );
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useUnassignSCBOneDeviceFromOrganization(): ApiCall<
  { serials: string[]; organizationId: string },
  void
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (a: { serials: string[]; organizationId: string }) => {
    const result = await authorizedFetch(
      `organizations/${a.organizationId}/scb-ones`,
      { method: "delete", body: JSON.stringify(a.serials) }
    );
    if (!result.ok) {
      throw result.status;
    }
  };
}

export function useSCBOneOperationRuns(
  serial: string,
  fromToFilter: FromToFilter
): PagedApiState<SCBOneOperationRun> {
  const refreshCounter = useRefreshCounter();
  const url = `scb-ones/${serial}/operation-runs?`;
  const appendDateFilter = useFromToFilter(fromToFilter);

  const get = useGetAuthorized<PagedResponse<SCBOneOperationRun>>(
    url,
    undefined,
    appendDateFilter
  );

  return usePagedApiState(get, refreshCounter);
}

export function useSCBOneOperationRunPaginated(
  runId: string | undefined,
  serial: string | undefined
): PagedApiState<Position> {
  const refreshCounter = useRefreshCounter();
  const url = `scb-ones/${serial}/operation-runs/${runId}`;

  const get = useGetAuthorized<PagedResponse<Position>>(
    url,
    undefined,
    undefined,
    !runId || !serial // if undefined, abort fetch
  );
  return usePagedApiState(get, refreshCounter);
}

export function useSCBOneOperationRunLimit(
  runId: string | undefined,
  serial: string | undefined,
  limit?: string
): ApiState<ApiResponse<Position[]> | undefined> {
  let url = `scb-ones/${serial}/operation-runs/${runId}`;

  // if no limit provided, fetch all positions
  if (limit) {
    url += `?limit=${limit}`;
  }

  return useApiState(
    useGetAuthorized<ApiResponse<Position[]>>(
      url,
      undefined,
      undefined,
      !runId || !serial // if undefined, abort fetch
    ),
    undefined
  );
}

export function useSCBOneHistoryAlerts(
  serial: string,
  fromToFilter: {
    from: Date;
    to: Date;
  }
): PagedApiState<Alert> {
  const refreshCounter = useRefreshCounter();
  const url = `scb-ones/${serial}/history/alerts?start=${fromToFilter.from?.toISOString()}&end=${
    fromToFilter.to?.toISOString() ?? new Date()
  }`;

  const get = useGetAuthorized<PagedResponse<Alert>>(url, undefined);
  return usePagedApiState(get, refreshCounter);
}

export function useSCBOneConfiguration(
  serial: string
): ApiState<GetSCBOneConfigurationStateResponse | undefined> {
  return useApiState(
    useGetAuthorized<GetSCBOneConfigurationStateResponse>(
      `scb-ones/${serial}/configuration`
    ),
    undefined
  );
}

export function useConfigureSCBOne(): ApiCall<
  { serial: string; configuration: DesiredSCBOneConfig },
  UpdateResult
> {
  const authorizedFetch = useAuthorizedFetch();
  return async (input: {
    serial: string;
    configuration: DesiredSCBOneConfig;
  }) => {
    const result = await authorizedFetch(
      `scb-ones/${input.serial}/configuration`,
      { method: "put", body: JSON.stringify(input.configuration) }
    );

    if (!result.ok) {
      const errorResponse: ApiErrorResponse = await result.json();
      return {
        error: true,
        response: errorResponse,
      };
    } else {
      return {
        error: false,
        response: {
          statusCode: result.status,
          detail: { message: "Configuration updated successfully" },
        },
      };
    }
  };
}
